import { createClerkSupabaseClient } from '@/lib/supabase'
import type { Database } from '@/interfaces/database.types'
import type { ToolType } from '@/interfaces/toolData'
import type { 
  DeviceTimeShift, 
  TimeShiftConfig
} from '@/interfaces/models/TimeShift'

import type { ServiceAuthProps } from '@/interfaces/serviceAuth'

type DeviceTimeShiftRow = Database['public']['Tables']['device_time_shifts']['Row']
type DeviceTimeShiftInsert = Database['public']['Tables']['device_time_shifts']['Insert']
type DeviceTimeShiftUpdate = Database['public']['Tables']['device_time_shifts']['Update']

/**
 * Service for managing device time shift configurations
 * Handles CRUD operations with immediate persistence
 */
export class TimeShiftService {
  private sessionProps: ServiceAuthProps

  constructor(sessionProps: ServiceAuthProps) {
    console.log('🔄 Creating new TimeShiftService', { userId: sessionProps.userId, orgId: sessionProps.orgId });
    this.sessionProps = sessionProps
  }

  /**
   * Get all time shifts for a specific project
   */
  async getTimeShifts(projectId: string): Promise<TimeShiftConfig> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    
    const { data, error } = await supabase
      .from('device_time_shifts')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: true })

    if (error) {
      console.error('Failed to fetch time shifts:', error)
      throw new Error(`Failed to fetch time shifts: ${error.message}`)
    }

    // Convert database rows to TimeShiftConfig
    const shifts = new Map<string, DeviceTimeShift>()
    
    data.forEach((row: DeviceTimeShiftRow) => {
      const deviceKey = row.device_type // Use device type as the key now
      shifts.set(deviceKey, {
        id: row.id,
        projectId: row.project_id,
        deviceType: row.device_type as ToolType,
        timeOffsetMs: Number(row.time_offset_ms),
        createdAt: row.created_at || undefined,
        updatedAt: row.updated_at || undefined,
        userId: row.user_id,
        organizationId: row.organization_id
      })
    })

    return {
      projectId,
      shifts
    }
  }

  /**
   * Save or update a time shift for a specific device
   * Simple, immediate operation - debouncing should be handled at UI layer
   * Returns the saved/updated TimeShift object
   */
  async saveTimeShift(
    projectId: string, 
    deviceType: ToolType, 
    offsetMs: number
  ): Promise<DeviceTimeShift> {
    return await this._saveTimeShiftImmediate(projectId, deviceType, offsetMs)
  }

  /**
   * Get organization ID from stable session props
   */
  private async getOrganizationId(): Promise<string> {
    if (!this.sessionProps.orgId) {
      throw new Error('TIMESHIFT_SERVICE: No organization ID available - user must select an organization')
    }
    return this.sessionProps.orgId
  }

  /**
   * Get user ID from stable session props
   */
  private getUserId(): string {
    return this.sessionProps.userId
  }

  /**
   * Internal method to save time shift immediately using simple upsert
   * Now works perfectly with simplified unique constraint
   */
  private async _saveTimeShiftImmediate(
    projectId: string, 
    deviceType: ToolType, 
    offsetMs: number
  ): Promise<DeviceTimeShift> {
    const supabase = createClerkSupabaseClient(this.sessionProps)
    const userId = this.getUserId()
    const organizationId = await this.getOrganizationId()
    
    const upsertData: DeviceTimeShiftInsert = {
      project_id: projectId,
      device_type: deviceType,
      time_offset_ms: offsetMs,
      user_id: userId,
      organization_id: organizationId
    }

    console.log(`🔍 TimeShiftService upserting data:`, {
      ...upsertData,
      session: {
        userId: this.sessionProps.userId,
        orgId: this.sessionProps.orgId
      }
    })

    // Simple upsert - works perfectly with single unique constraint
    const { data, error } = await supabase
      .from('device_time_shifts')
      .upsert(upsertData, {
        onConflict: 'project_id,device_type'
      })
      .select()
      .single()

    if (error) {
      console.error(`❌ TimeShiftService upsert failed:`, {
        error,
        upsertData,
        sessionData: {
          userId: this.sessionProps.userId,
          orgId: this.sessionProps.orgId
        }
      })
      throw new Error(`Failed to save time shift: ${error.message}`)
    }

    console.log(`✅ TimeShiftService upsert successful:`, data)

    return this._mapRowToDeviceTimeShift(data)
  }

  /**
   * Delete a specific time shift
   */
  async deleteTimeShift(
    projectId: string, 
    deviceType: ToolType
  ): Promise<void> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { error } = await supabase
      .from('device_time_shifts')
      .delete()
      .eq('project_id', projectId)
      .eq('device_type', deviceType)

    if (error) {
      throw new Error(`Failed to delete time shift: ${error.message}`)
    }
  }

  /**
   * Reset all time shifts for a project (delete all)
   */
  async resetAllTimeShifts(projectId: string): Promise<void> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    const { error } = await supabase
      .from('device_time_shifts')
      .delete()
      .eq('project_id', projectId)

    if (error) {
      throw new Error(`Failed to reset time shifts: ${error.message}`)
    }


  }



  /**
   * Bulk save multiple time shifts efficiently
   * Useful for importing configurations or batch operations
   */
  async bulkSaveTimeShifts(
    projectId: string, 
    timeShifts: Array<{
      deviceType: ToolType
      offsetMs: number
    }>
  ): Promise<DeviceTimeShift[]> {
    const supabase = createClerkSupabaseClient(this.sessionProps)

    // First, get existing time shifts to determine updates vs inserts
    const { data: existing } = await supabase
      .from('device_time_shifts')
      .select('*')
      .eq('project_id', projectId)

    const existingMap = new Map<ToolType, DeviceTimeShiftRow>()
    existing?.forEach(row => {
      existingMap.set(row.device_type as ToolType, row)
    })

    const updates: Array<{ id: string; data: DeviceTimeShiftUpdate }> = []
    const inserts: DeviceTimeShiftInsert[] = []

    timeShifts.forEach(({ deviceType, offsetMs }) => {
      const existingRow = existingMap.get(deviceType)

      if (existingRow) {
        updates.push({
          id: existingRow.id,
          data: {
            time_offset_ms: offsetMs,
            updated_at: new Date().toISOString()
          }
        })
      } else {
        inserts.push({
          project_id: projectId,
          device_type: deviceType,
          time_offset_ms: offsetMs,
          user_id: this.sessionProps.userId || 'unknown',
          organization_id: this.sessionProps.orgId || 'unknown'
        })
      }
    })

    const results: DeviceTimeShift[] = []

    // Execute updates
    for (const { id, data } of updates) {
      const { data: updated, error } = await supabase
        .from('device_time_shifts')
        .update(data)
        .eq('id', id)
        .select()
        .single()

      if (error) {
        console.error(`Failed to update time shift ${id}:`, error)
      } else {
        results.push(this._mapRowToDeviceTimeShift(updated))
      }
    }

    // Execute inserts
    if (inserts.length > 0) {
      const { data: inserted, error } = await supabase
        .from('device_time_shifts')
        .insert(inserts)
        .select()

      if (error) {
        console.error('Failed to bulk insert time shifts:', error)
      } else {
        results.push(...inserted.map(this._mapRowToDeviceTimeShift))
      }
    }

    return results
  }

  /**
   * Helper method to map database row to DeviceTimeShift interface
   */
  private _mapRowToDeviceTimeShift(row: DeviceTimeShiftRow): DeviceTimeShift {
    return {
      id: row.id,
      projectId: row.project_id,
      deviceType: row.device_type as ToolType,
      timeOffsetMs: Number(row.time_offset_ms),
      createdAt: row.created_at || undefined,
      updatedAt: row.updated_at || undefined,
      userId: row.user_id,
      organizationId: row.organization_id
    }
  }


}

/**
 * Create a new TimeShiftService instance
 * This factory function ensures proper session handling
 */
export const createTimeShiftService = (sessionProps: ServiceAuthProps): TimeShiftService => {
  return new TimeShiftService(sessionProps)
} 